import { Box, styled, useTheme } from '@mui/material';
import { memo } from 'react';

import {
  capitalizeWords,
  getIsItalyRegionId,
  GlowField,
  ICONS,
  parseItalianState,
} from '#shared';

import type { NewCase } from '../../types';

interface NewCaseCardContentFullProps {
  data: NewCase & {
    updatedFields?: string[];
    isNewOnBoard?: boolean;
  };
  highlightDuration?: number;
  width?: number;
}

const PREFIX = 'TSMED-NewCaseCardContentFull';

const newCaseCardContentFullClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,

  caseName: `${PREFIX}-caseName`,
  oscStatus: `${PREFIX}-oscStatus`,
  locationProgress: `${PREFIX}-locationProgress`,
  path: `${PREFIX}-path`,
  pathFrom: `${PREFIX}-pathFrom`,
  pathTo: `${PREFIX}-pathTo`,
  timeInfo: `${PREFIX}-timeInfo`,
  donorOrTime: `${PREFIX}-donorOrTime`,
  caseTimeEnd: `${PREFIX}-caseTimeEnd`,
  location: `${PREFIX}-location`,
};

interface RootProps {
  highlightBorder?: boolean;
  isActiveStatus?: boolean;
}

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop =>
    prop !== 'highlightBorder' && prop !== 'isActiveStatus',
})<RootProps>(({ theme: { palette, spacing, utils } }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  [`.${newCaseCardContentFullClasses.locationProgress}`]: {
    position: 'relative',
    height: 35,
    [`&:before`]: {
      content: '""',
      position: 'absolute',
      top: 'calc(50% - 2px)',
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: palette.background.default,
    },
    [`&:after`]: {
      content: '""',
      position: 'absolute',
      top: 'calc(50% - 2.5px)',
      right: 0,
      height: 5,
      width: 5,
      borderRadius: '50%',
      border: `1.25px solid ${palette.mapBlue[400]}`,
    },
    svg: {
      position: 'absolute',
      fill: 'currentColor',
      height: utils.clamp(28, 35),
      width: utils.clamp(28, 35),
      padding: spacing(0.5),
    },
  },
  [`.${newCaseCardContentFullClasses.path}`]: {
    display: 'flex',
    flexWrap: 'nowrap',
    fontSize: utils.clamp(12, 14),
    justifyContent: 'space-between',
    gap: spacing(1),
    [`.${newCaseCardContentFullClasses.pathFrom}`]: {
      color: palette.grey[100],
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    [`.${newCaseCardContentFullClasses.pathTo}`]: {},
  },
}));

export const NewCaseCardContentFull = memo<NewCaseCardContentFullProps>(
  ({ data: newCase, highlightDuration }) => {
    const { palette } = useTheme();

    const Icon = ICONS[newCase.organ.name];
    const [city, state] = newCase.donorHospital?.address.split(', ') ?? [
      '---',
      '---',
    ];
    const isItalian = getIsItalyRegionId(newCase.serviceRegionId ?? '');
    const stateToRender = isItalian
      ? parseItalianState(state)
      : state.toUpperCase();
    const fromLabel = `${capitalizeWords(city)},${stateToRender}`;
    const toLabel = newCase.transplantCenterCode ?? '---';

    return (
      <Root className={newCaseCardContentFullClasses.location}>
        <Box className={newCaseCardContentFullClasses.locationProgress}>
          <GlowField
            startColor={palette.orange[500]}
            deps={[Icon]}
            component={Icon}
            ms={highlightDuration}
          />
        </Box>

        <Box className={newCaseCardContentFullClasses.path}>
          <GlowField
            startColor={palette.grey[100]}
            deps={[fromLabel]}
            className={newCaseCardContentFullClasses.pathFrom}
            ms={highlightDuration}
          >
            {fromLabel}
          </GlowField>
          <GlowField
            startColor={palette.orange[500]}
            deps={[toLabel]}
            className={newCaseCardContentFullClasses.pathTo}
            ms={highlightDuration}
          >
            {toLabel}
          </GlowField>
        </Box>
      </Root>
    );
  },
);

NewCaseCardContentFull.displayName = 'NewCaseCardContent';
